import type { ValidationError } from '@/lib/graphql/generated/graphql';
import { ValidationRuleId } from '@/lib/graphql/generated/graphql';

/**
 * Règles purement "Choose" : non actionnables côté marque (c'est à Choose de les
 * régler), donc masquées pour les utilisateurs non-internes (marques). Comme la marque
 * n'a aucun moyen de les corriger, elles ne doivent ni s'afficher ni bloquer sa
 * validation — d'où un masquage **agnostique à la sévérité** (warning comme error).
 * Ajouter ici de futures règles Choose-only.
 */
export const BRAND_HIDDEN_RULE_IDS: ReadonlySet<ValidationRuleId> = new Set([
  ValidationRuleId.PsychologicalPriceApplication,
  ValidationRuleId.PriceCompetitiveness,
  ValidationRuleId.NegativeMargin,
]);

const isBrandHidden = (error: Pick<ValidationError, 'ruleId'>): boolean =>
  BRAND_HIDDEN_RULE_IDS.has(error.ruleId);

/**
 * Filtre les règles Choose-only pour les marques ; renvoie la liste inchangée
 * (même référence) pour les utilisateurs internes.
 *
 * - Internes : early-return de la même référence (aucune allocation, stabilité référentielle).
 * - Marques sans règle masquée (cas majoritaire) : early-return de la même référence également,
 *   pour éviter d'allouer un nouveau tableau à chaque render et de casser les `useMemo` en aval.
 * - Marques avec règle(s) masquée(s) : retire toute erreur/warning dont le `ruleId` est listé,
 *   quelle que soit sa `severity` (ces règles relèvent entièrement de Choose).
 */
export function filterBrandVisibleValidationErrors<
  T extends Pick<ValidationError, 'ruleId'>,
>(errors: T[], isInternalUser: boolean): T[] {
  if (isInternalUser) return errors;
  if (!errors.some(isBrandHidden)) return errors;
  return errors.filter((error) => !isBrandHidden(error));
}
